/* eslint-disable no-irregular-whitespace */
import React, { useState, useEffect } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { AuthState } from "store/ducks/auth/types";
import {
  birthdayShownInSession,
  creciShownInSession,
  loadUserCriticalData,
  saveUserCriticalData,
  markBirthdayAsShown,
  markCreciAsShown,
  CRECI_WARNING_STAGES,
  CRECI_CACHE_KEY,
} from "../../utils/userCriticalData";
import * as S from "./styles";
import CarouselContent from "./components/CarouselContent";
import DestaquesContent from "./components/DestaquesContent";
import CarometroContent from "./components/CarometroContent";
import BirthdayAnimation from "./components/BirthdayAnimation";
import EnvelopeAnimation from "./components/EnvelopeAnimation";
import InternshipExpirationModal from "./components/InternshipExpirationModal";

function HomeNew() {
  const {
    name,
    fazAniversarioHoje: userFazAniversarioHoje,
    dataVencimentoCreci: userDataVencimentoCreci,
    dataNascimento: userDataNascimento,
  } = useSelector<RootStateOrAny, AuthState>((state) => state.user);

  // Estados para dados críticos
  const [dataNascimento, setDataNascimento] = useState<string | null>(
    userDataNascimento || null
  );
  const [dataVencimentoCreci, setDataVencimentoCreci] = useState<string | null>(
    userDataVencimentoCreci || null
  );
  const [fazAniversarioHoje, setFazAniversarioHoje] = useState<boolean>(
    userFazAniversarioHoje || false
  );

  // Estados para exibição
  const [showAnimations, setShowAnimations] = useState(false);
  const [showExpirationModal, setShowExpirationModal] = useState(false);
  const [internshipData, setInternshipData] = useState({
    daysRemaining: 0,
    userName: "",
  });
  const [showBirthday, setShowBirthday] = useState(false);
  const [showEnvelope, setShowEnvelope] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Carrega dados do localStorage e do Redux, com prioridade para o Redux
  const loadUserData = () => {
    try {
      // Tentar pegar do Redux primeiro (dados mais atualizados)
      let dataFound = false;

      if (
        userDataNascimento ||
        userDataVencimentoCreci ||
        userFazAniversarioHoje
      ) {
        setDataNascimento(userDataNascimento || null);
        setDataVencimentoCreci(userDataVencimentoCreci || null);
        setFazAniversarioHoje(userFazAniversarioHoje || false);
        dataFound = true;

        // Atualizar também no localStorage para persistência
        const dataToSave = {
          dataNascimento: userDataNascimento || null,
          dataVencimentoCreci: userDataVencimentoCreci || null,
          fazAniversarioHoje: userFazAniversarioHoje || false,
          lastUpdated: new Date().toISOString(),
        };

        saveUserCriticalData(dataToSave);
      }

      // Se não encontrou no Redux, verificar no localStorage
      if (!dataFound) {
        const criticalData = loadUserCriticalData();
        if (criticalData) {
          setDataNascimento(criticalData.dataNascimento);
          setDataVencimentoCreci(criticalData.dataVencimentoCreci);
          setFazAniversarioHoje(criticalData.fazAniversarioHoje || false);
          dataFound = true;
        }
      }

      setDataLoaded(true);
      return dataFound;
    } catch (error) {
      console.error("[HomeNew] Erro ao carregar dados críticos:", error);
      setDataLoaded(true);
    }
    return false;
  };

  // Verifica se o aviso de CRECI já foi exibido nas últimas 24 horas
  const creciShownInLast24Hours = (): boolean => {
    const lastShownStr = localStorage.getItem(CRECI_CACHE_KEY);
    if (!lastShownStr) return false;

    try {
      const lastShown = JSON.parse(lastShownStr);
      // Verificar se já mostramos o aviso para o estágio atual
      return (
        lastShown.stage?.toString() === internshipData.daysRemaining.toString()
      );
    } catch (error) {
      console.error("Erro ao verificar intervalo para CRECI:", error);
      return false;
    }
  };

  // Verifica aniversário - deve aparecer a cada nova sessão/login
  const checkBirthday = () => {
    try {
      if (birthdayShownInSession()) {
        return false;
      }

      if (!dataNascimento) return false;

      const today = new Date();
      const birthDateParts = dataNascimento.split("-");
      if (birthDateParts.length !== 3) return false;

      const birthMonth = parseInt(birthDateParts[1], 10);
      const birthDay = parseInt(birthDateParts[2], 10);
      const currentMonth = today.getMonth() + 1;
      const currentDay = today.getDate();

      return currentDay === birthDay && currentMonth === birthMonth;
    } catch (error) {
      console.error("Erro ao verificar aniversário:", error);
      return false;
    }
  };

  // Calcula dias restantes até vencimento do CRECI
  const calculateRemainingDays = () => {
    if (!dataVencimentoCreci) return 0;

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset para início do dia

      // Converte data do formato DD/MM/YYYY para YYYY-MM-DD
      const [day, month, year] = dataVencimentoCreci.split("/");
      const expirationDate = new Date(`${year}-${month}-${day}`);
      expirationDate.setHours(0, 0, 0, 0); // Reset para início do dia

      if (Number.isNaN(expirationDate.getTime())) {
        console.error("[HomeNew] Data de CRECI inválida:", dataVencimentoCreci);
        return 0;
      }

      const diffTime = expirationDate.getTime() - today.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      return diffDays; // Retorna o valor real, mesmo que negativo
    } catch (error) {
      console.error("[HomeNew] Erro ao calcular dias restantes:", error);
      return 0;
    }
  };

  // Verifica se deve mostrar aviso do CRECI
  const shouldShowCreciWarning = (daysRemaining: number): boolean => {
    // Não mostrar se a data for inválida
    if (
      !dataVencimentoCreci ||
      Number.isNaN(
        new Date(dataVencimentoCreci.split("/").reverse().join("-")).getTime()
      )
    ) {
      return false;
    }

    // Verifica se está dentro do período de aviso
    if (daysRemaining < 0 || daysRemaining > CRECI_WARNING_STAGES[0]) {
      return false;
    }

    // Verifica se está em um dos estágios de aviso
    const isInWarningStage = CRECI_WARNING_STAGES.includes(daysRemaining);
    if (!isInWarningStage) return false;

    // Verificar se já mostramos o aviso nesta sessão
    if (creciShownInSession()) {
      return false;
    }

    // Verificar qual foi o último estágio mostrado (em qualquer sessão)
    const lastShownStr = localStorage.getItem(CRECI_CACHE_KEY);
    if (!lastShownStr) {
      return true;
    }

    try {
      const lastShown = JSON.parse(lastShownStr);

      // Se o estágio é o mesmo que já foi mostrado antes, mas estamos em uma nova sessão,
      // devemos mostrar novamente
      return true;
    } catch (error) {
      console.error("[HomeNew] Erro ao verificar estágio do CRECI:", error);
      return true;
    }
  };

  // Calcula e verifica dados do CRECI
  const fetchInternshipData = () => {
    try {
      if (!dataVencimentoCreci) {
        console.log("[HomeNew] Sem data de vencimento do CRECI para verificar");
        return;
      }

      const daysRemaining = calculateRemainingDays();

      console.log("[HomeNew] Dias restantes para CRECI:", daysRemaining);

      setInternshipData({
        daysRemaining,
        userName: name || "Usuário",
      });

      const shouldShow = shouldShowCreciWarning(daysRemaining);

      console.log("[HomeNew] Deve mostrar aviso CRECI:", shouldShow);

      if (shouldShow) {
        setShowExpirationModal(true);

        // Marcar como mostrado nesta sessão
        markCreciAsShown();

        // Registrar no localStorage o estágio atual (persistente entre sessões)
        const cacheData = {
          stage: daysRemaining,
        };
        localStorage.setItem(CRECI_CACHE_KEY, JSON.stringify(cacheData));
      }
    } catch (error) {
      console.error("[HomeNew] Erro ao verificar dados do CRECI:", error);
    }
  };

  // Verifica e mostra notificações
  const checkAndShowNotifications = () => {
    try {
      // Verificar aniversário
      const isBirthday = checkBirthday();
      console.log("[HomeNew] É aniversário:", isBirthday);

      if (isBirthday) {
        setShowEnvelope(true);
        setShowAnimations(true);
        setShowBirthday(true);
        markBirthdayAsShown();
      }

      // Verificar CRECI
      fetchInternshipData();
    } catch (error) {
      console.error("[HomeNew] Erro ao verificar notificações:", error);
    }
  };

  // Efeito para carregar dados ao inicializar
  useEffect(() => {
    loadUserData();
  }, []);

  // Efeito para verificar notificações após carregar dados
  useEffect(() => {
    if (dataLoaded) {
      const notificationTimer = setTimeout(() => {
        checkAndShowNotifications();
      }, 1500);

      return () => {
        clearTimeout(notificationTimer);
      };
    }
    return () => {};
  }, [dataLoaded]);

  // Monitorar mudanças nos dados vindos do Redux
  useEffect(() => {
    if (
      userDataNascimento !== dataNascimento ||
      userDataVencimentoCreci !== dataVencimentoCreci
    ) {
      loadUserData();
    }
  }, [userDataNascimento, userDataVencimentoCreci]);

  // Ouvir eventos de atualização de dados
  useEffect(() => {
    const handleUserDataUpdated = () => {
      console.log("[HomeNew] Evento user-data-updated recebido");
      if (loadUserData()) {
        checkAndShowNotifications();
      }
    };

    window.addEventListener("user-data-updated", handleUserDataUpdated);
    return () => {
      window.removeEventListener("user-data-updated", handleUserDataUpdated);
    };
  }, []);

  // Manipuladores de eventos
  const handleCloseAnimations = () => {
    setShowAnimations(false);
    setShowBirthday(false);
    setShowEnvelope(false);
  };

  return (
    <>
      <S.Content>
        <CarouselContent />
        <S.SpaceY space={24} />
        <DestaquesContent />
        <S.SpaceY space={24} />
        <CarometroContent />
      </S.Content>

      {showAnimations && (
        <>
          <S.AnimationsContainer />
          {showBirthday && (
            <BirthdayAnimation
              visible={showAnimations}
              onClose={handleCloseAnimations}
            />
          )}
          {showEnvelope && (
            <EnvelopeAnimation
              visible={showAnimations}
              onClose={handleCloseAnimations}
            />
          )}
        </>
      )}

      <InternshipExpirationModal
        daysRemaining={internshipData.daysRemaining}
        open={showExpirationModal}
        setOpen={setShowExpirationModal}
      />
    </>
  );
}

export default HomeNew;
